import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { LuCheck, LuPlay, LuX } from 'react-icons/lu';
import Button from '../../../../components/ui/Button';
import { useTranslation } from '../../../../i18n/LanguageContext';
import { buildTimeline } from './timeline';

const WATCHED_KEY = 'habcare.calendarTutorial.watched';
const DISMISSED_KEY = 'habcare.calendarTutorial.bannerDismissed';

const readFlag = (key) => {
  try {
    return window.localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
};
const writeFlag = (key) => {
  try {
    window.localStorage.setItem(key, 'true');
  } catch {
    // Depolama kapalıysa yalnızca bu oturumda geçerli
  }
};

/**
 * Who has seen the guide: the banner stays until the video is watched (90%)
 * or put away with "Daha sonra"; the toolbar button is always there.
 */
export const useCalendarTutorial = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [watched, setWatched] = useState(() => readFlag(WATCHED_KEY));
  const [dismissed, setDismissed] = useState(() => readFlag(DISMISSED_KEY));
  const minutes = useMemo(() => Math.max(1, Math.round(buildTimeline(t).total / 60000)), [t]);

  const markWatched = useCallback(() => {
    writeFlag(WATCHED_KEY);
    setWatched(true);
  }, []);
  const dismiss = useCallback(() => {
    writeFlag(DISMISSED_KEY);
    setDismissed(true);
  }, []);

  return {
    isOpen,
    open: useCallback(() => setIsOpen(true), []),
    close: useCallback(() => setIsOpen(false), []),
    watched,
    showBanner: !watched && !dismissed,
    minutes,
    markWatched,
    dismiss,
  };
};

/** Big card at the top of the calendar for people who haven't watched it yet. */
export const CalendarTutorialBanner = ({ minutes, onWatch, onDismiss }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.tutorial.cta.${key}`, vars);
  return (
    <div className="tw-relative tw-overflow-hidden tw-rounded-[1.5rem] tw-border tw-border-teal-500/25 tw-bg-gradient-to-r tw-from-teal-500/[0.12] tw-via-cyan-500/[0.08] tw-to-indigo-500/[0.08] tw-p-4 sm:tw-p-5 dark:tw-border-teal-400/20">
      <span aria-hidden="true" className="tw-pointer-events-none tw-absolute -tw-right-16 -tw-top-20 tw-h-56 tw-w-56 tw-rounded-full tw-bg-teal-400/20 tw-blur-3xl" />
      <button
        type="button"
        onClick={onDismiss}
        aria-label={label('later')}
        title={label('later')}
        className="tw-absolute tw-right-3 tw-top-3 tw-grid tw-h-8 tw-w-8 tw-place-items-center tw-rounded-full tw-text-slate-500 tw-transition-colors hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-800 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-text-slate-400 dark:hover:tw-bg-white/10 dark:hover:tw-text-white"
      >
        <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
      </button>
      <div className="tw-relative tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row sm:tw-items-center">
        <button
          type="button"
          onClick={onWatch}
          aria-label={label('watch')}
          className="tw-group tw-relative tw-grid tw-h-24 tw-w-full tw-shrink-0 tw-place-items-center tw-overflow-hidden tw-rounded-2xl tw-bg-gradient-to-br tw-from-teal-500 tw-to-cyan-700 tw-shadow-lg tw-shadow-teal-500/30 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/60 sm:tw-w-40"
        >
          <span aria-hidden="true" className="tw-absolute tw-inset-x-3 tw-top-3 tw-grid tw-grid-cols-7 tw-gap-1 tw-opacity-30">
            {Array.from({ length: 21 }, (_, index) => <span key={index} className="tw-h-3 tw-rounded-sm tw-bg-white/70" />)}
          </span>
          <span aria-hidden="true" className="tw-absolute tw-h-14 tw-w-14 tw-animate-ping tw-rounded-full tw-bg-white/30" />
          <span className="tw-relative tw-grid tw-h-12 tw-w-12 tw-place-items-center tw-rounded-full tw-bg-white tw-text-teal-600 tw-shadow-xl tw-transition-transform group-hover:tw-scale-110">
            <LuPlay aria-hidden="true" className="tw-ml-0.5 tw-h-6 tw-w-6" />
          </span>
        </button>
        <div className="tw-min-w-0 tw-flex-1 sm:tw-pr-8">
          <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-teal-700 dark:tw-text-teal-300">{label('eyebrow', { minutes })}</p>
          <p className="tw-mt-1 tw-text-lg tw-font-semibold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">{label('title')}</p>
          <p className="tw-mt-1 tw-max-w-2xl tw-text-sm tw-leading-relaxed tw-text-slate-600 dark:tw-text-slate-300">{label('text')}</p>
          <div className="tw-mt-3 tw-flex tw-flex-wrap tw-gap-2">
            <Button variant="primary" onClick={onWatch}>
              <LuPlay aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('watch')}
            </Button>
            <Button variant="ghost" onClick={onDismiss}>{label('later')}</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

CalendarTutorialBanner.propTypes = {
  minutes: PropTypes.number.isRequired,
  onWatch: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

/** Always-visible button next to "Nasıl okunur?"; pulses until the video has been watched. */
export const CalendarTutorialButton = ({ onClick, watched }) => {
  const { t } = useTranslation();
  const label = (key) => t(`profilePage.tutorial.cta.${key}`);
  return (
    <button
      type="button"
      onClick={onClick}
      title={label('buttonHint')}
      className="tw-relative tw-inline-flex tw-h-8 tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-gradient-to-r tw-from-teal-500 tw-to-cyan-600 tw-pl-1 tw-pr-3 tw-text-xs tw-font-semibold tw-text-white tw-shadow-md tw-shadow-teal-500/30 tw-transition-transform hover:tw-scale-[1.03] active:tw-scale-95 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/60"
    >
      {!watched && <span aria-hidden="true" className="tw-absolute tw-inset-0 tw-animate-ping tw-rounded-full tw-bg-teal-400/30" />}
      <span className="tw-relative tw-grid tw-h-6 tw-w-6 tw-place-items-center tw-rounded-full tw-bg-white tw-text-teal-600">
        <LuPlay aria-hidden="true" className="tw-ml-px tw-h-3.5 tw-w-3.5" />
      </span>
      <span className="tw-relative">{label('button')}</span>
      {watched && <LuCheck aria-label={label('watched')} className="tw-relative tw-h-3.5 tw-w-3.5 tw-opacity-80" />}
    </button>
  );
};

CalendarTutorialButton.propTypes = { onClick: PropTypes.func.isRequired, watched: PropTypes.bool };
